import { vetor, type Vetor3 } from '../geometria/vetor3'
import {
  colunaDaRetencao,
  zDaColunaNaAltura,
  type AncoraOcupante,
  type ConfiguracaoCinto,
  type PerfilVeiculo,
} from './perfilVeiculo'

/**
 * Pontos do conjunto de retenção de uma posição de ocupante.
 *
 * Fica no domínio, e não no componente 3D, porque é geometria derivada do
 * perfil: assim dá para verificar por teste que nenhuma ancoragem cai fora da
 * carroceria — coisa que a perspectiva da cena esconde com facilidade.
 */
export interface PontosCinto {
  /**
   * Por onde a faixa diagonal desce para o ombro: guia na coluna, ou a fenda da
   * prateleira traseira no sedan. Ausente no cinto subabdominal.
   */
  ancoragemSuperior: Vetor3 | null
  /** a fita sai da fenda da prateleira traseira, não de uma guia na coluna */
  saidaNaPrateleira: boolean
  /** ponto do ombro por onde a faixa diagonal passa */
  ombro: Vetor3 | null
  ancoragemInferiorExterna: Vetor3
  ancoragemInferiorInterna: Vetor3
  fecho: Vetor3
  quadrilExterno: Vetor3
  /**
   * Carretel que recolhe a fita: pé da coluna B na frente; atrás, coluna C no
   * hatch e prateleira traseira no sedan. Ausente no cinto subabdominal.
   */
  retrator: Vetor3 | null
  /** pretensionador de retrator, no próprio retrator; ausente quando o perfil não o declara */
  pretensionador: Vetor3 | null
  /** pretensionador de fecho, abaixo do fecho; ausente quando o perfil não o declara */
  pretensionadorFecho: Vetor3 | null
}

/** Altura da ancoragem superior acima da linha de cintura. */
const ALTURA_ANCORAGEM_SUPERIOR = 0.22
/** Topo da prateleira traseira acima da linha de cintura: rente ao alto do encosto. */
const ALTURA_PRATELEIRA = 0.07
/** Afastamento da ancoragem em relação à chapa lateral. */
const RECUO_LATERAL = 0.12

export function pontosDoCinto(
  perfil: PerfilVeiculo,
  ancora: AncoraOcupante,
  config: ConfiguracaoCinto,
): PontosCinto {
  const { x, y, z } = ancora.posicao
  const central = ancora.lado === 'central'
  const sinal = ancora.lado === 'esquerdo' ? 1 : -1
  const tresPontos = config.tipo === 'tresPontos'

  // o ocupante central não tem parede lateral: as ancoragens nascem no banco
  const xExterno = central ? x - 0.22 : sinal * (perfil.largura / 2 - RECUO_LATERAL)
  const coluna = colunaDaRetencao(perfil, ancora.fila)
  const yAncoragem = perfil.cinturaY + ALTURA_ANCORAGEM_SUPERIOR

  const ombro = tresPontos ? vetor(x + sinal * 0.12, y + 0.44, z + 0.08) : null
  const retrator = tresPontos ? posicaoRetrator(perfil, ancora, xExterno) : null
  // com o retrator na prateleira, a fita sobe direto pela fenda do acabamento
  const saidaNaPrateleira = tresPontos && retratorNaPrateleira(perfil, ancora)
  const ancoragemSuperior = !tresPontos
    ? null
    : saidaNaPrateleira && retrator
      ? vetor(retrator.x, prateleiraTraseira(perfil)!.yTopo, retrator.z)
      : vetor(xExterno, yAncoragem, zDaColunaNaAltura(perfil, coluna, yAncoragem))
  const fecho = vetor(x - sinal * 0.2, ancora.alturaAssento + 0.04, z + 0.02)

  return {
    ancoragemSuperior,
    saidaNaPrateleira,
    ombro,
    ancoragemInferiorExterna: vetor(xExterno, ancora.alturaAssento - 0.04, z - 0.16),
    ancoragemInferiorInterna: vetor(
      central ? x + 0.22 : x - sinal * 0.24,
      ancora.alturaAssento - 0.02,
      z - 0.14,
    ),
    fecho,
    quadrilExterno: vetor(x + sinal * 0.19, ancora.alturaAssento + 0.05, z + 0.04),
    retrator,
    pretensionador: config.pretensionador && retrator ? retrator : null,
    pretensionadorFecho: config.pretensionadorFecho
      ? vetor(fecho.x, ancora.alturaAssento - 0.1, fecho.z - 0.04)
      : null,
  }
}

/**
 * Prateleira (tampão) entre o encosto traseiro e o vidro traseiro do sedan.
 * O hatch não tem: a tampa traseira abre o porta-malas junto com o vidro.
 */
export function prateleiraTraseira(perfil: PerfilVeiculo): { yTopo: number; zFrente: number; zTras: number } | null {
  if (perfil.volumeTraseiro !== 'sedan') return null
  const traseiras = perfil.ancoras.filter((a) => a.fila === 'traseira')
  if (traseiras.length === 0) return null
  // a frente encosta atrás do topo do encosto, que inclina para trás
  const zEncosto = Math.min(...traseiras.map((a) => a.posicao.z))
  return {
    yTopo: perfil.cinturaY + ALTURA_PRATELEIRA,
    zFrente: zEncosto - 0.34,
    zTras: perfil.colunaC.zBase,
  }
}

/** Sedan, fila traseira, posição lateral: o retrator vai sob a prateleira. */
function retratorNaPrateleira(perfil: PerfilVeiculo, ancora: AncoraOcupante): boolean {
  return ancora.fila === 'traseira' && ancora.lado !== 'central' && perfil.volumeTraseiro === 'sedan'
}

function posicaoRetrator(perfil: PerfilVeiculo, ancora: AncoraOcupante, xExterno: number): Vetor3 {
  if (ancora.fila === 'dianteira') {
    // dentro do acabamento inferior da coluna B, logo acima da soleira
    return vetor(xExterno, perfil.topoSoleiraY + 0.14, perfil.colunaB.z)
  }
  if (retratorNaPrateleira(perfil, ancora)) {
    // sob a prateleira, atrás do encosto e alinhado com o ombro do ocupante
    const sinal = ancora.lado === 'esquerdo' ? 1 : -1
    return vetor(ancora.posicao.x + sinal * 0.12, perfil.cinturaY, ancora.posicao.z - 0.38)
  }
  // hatch: no montante da coluna C, abaixo da cintura (a tampa não tem prateleira fixa)
  const coluna = colunaDaRetencao(perfil, ancora.fila)
  const y = perfil.cinturaY - 0.12
  return vetor(xExterno, y, zDaColunaNaAltura(perfil, coluna, y))
}

/** Todos os pontos não nulos, para percorrer o conjunto de uma vez. */
export function todosOsPontos(pontos: PontosCinto): Vetor3[] {
  return [
    pontos.ancoragemSuperior,
    pontos.ombro,
    pontos.ancoragemInferiorExterna,
    pontos.ancoragemInferiorInterna,
    pontos.fecho,
    pontos.quadrilExterno,
    pontos.retrator,
    pontos.pretensionadorFecho,
  ].filter((p): p is Vetor3 => p !== null)
}

/** O ponto está dentro do envelope da carroceria? */
export function dentroDaCarroceria(perfil: PerfilVeiculo, p: Vetor3): boolean {
  return (
    Math.abs(p.x) <= perfil.largura / 2 &&
    p.y >= perfil.pisoY - 0.2 &&
    p.y <= perfil.tetoY &&
    p.z <= perfil.frenteZ &&
    p.z >= perfil.traseiraZ
  )
}

/** O ponto está dentro do habitáculo, entre a coluna A e a coluna C? */
export function dentroDoHabitaculo(perfil: PerfilVeiculo, p: Vetor3): boolean {
  return (
    dentroDaCarroceria(perfil, p) &&
    p.z <= perfil.colunaA.zBase &&
    p.z >= perfil.colunaC.zBase - 0.02
  )
}
